import type { EventVoteInfo, VoteHistory } from "../../types/swagger";
import { loginMemberProfile, mockSeasonEvents } from "./rankingSeason";
import { mockMemberNames } from "./members";

// メンバーID → UUID マッピング（モック用）
export const mockMemberUuids: Record<string, string> = Object.fromEntries(
  Object.keys(mockMemberNames).map((id) => [
    id,
    `00000000-0000-0000-0000-${id.padStart(12, "0")}`,
  ]),
);

// 名前 → UUID
const getUuidByName = (name: string): string => {
  const entry = Object.entries(mockMemberNames).find(([, n]) => n === name);
  return entry ? (mockMemberUuids[entry[0]] ?? "") : "";
};

// UUID → 名前
const getNameByUuid = (uuid: string): string => {
  const entry = Object.entries(mockMemberUuids).find(([, u]) => u === uuid);
  return entry ? (mockMemberNames[entry[0]] ?? "") : "";
};

type InternalVoteEntry = {
  voter_uuid: string;
  voter_name: string;
  voted_uuid: string;
  voted_name: string;
};

// 投票インメモリストア: event_id → 投票エントリ[]
const mockVoteStore: Record<string, InternalVoteEntry[]> = {
  // Stage999（終了済み）
  "fd7909b0-c0fc-44f8-816e-77d20e5a3ddf": [
    {
      voter_uuid: getUuidByName("Akira menai"),
      voter_name: "Akira menai",
      voted_uuid: getUuidByName("Sho Aoki"),
      voted_name: "Sho Aoki",
    },
    {
      voter_uuid: getUuidByName("野澤 哲也"),
      voter_name: "野澤 哲也",
      voted_uuid: getUuidByName("Sho Aoki"),
      voted_name: "Sho Aoki",
    },
    {
      voter_uuid: getUuidByName("Max Verstappen"),
      voter_name: "Max Verstappen",
      voted_uuid: getUuidByName("Akira menai"),
      voted_name: "Akira menai",
    },
  ],
  // Spring Challenge（終了済み）
  "4d76a4c1-cf13-4c86-9df9-2da59ab4d8dc": [
    {
      voter_uuid: getUuidByName("Akira menai"),
      voter_name: "Akira menai",
      voted_uuid: getUuidByName("Sho Aoki"),
      voted_name: "Sho Aoki",
    },
    {
      voter_uuid: getUuidByName("野澤 哲也"),
      voter_name: "野澤 哲也",
      voted_uuid: getUuidByName("Akira menai"),
      voted_name: "Akira menai",
    },
  ],
  // February Challenge（終了済み）
  "74ea7389-c0ff-4857-8ca8-f94cc961e7a7": [
    {
      voter_uuid: getUuidByName("Akira menai"),
      voter_name: "Akira menai",
      voted_uuid: getUuidByName("Akira menai"),
      voted_name: "Akira menai",
    },
    {
      voter_uuid: getUuidByName("野澤 哲也"),
      voter_name: "野澤 哲也",
      voted_uuid: getUuidByName("Sho Aoki"),
      voted_name: "Sho Aoki",
    },
  ],
  // Summer Warmup（開催予定）
  "516d5f20-d9d8-4d75-89b1-2954bc1f2d95": [
    {
      voter_uuid: getUuidByName("野澤 哲也"),
      voter_name: "野澤 哲也",
      voted_uuid: getUuidByName("Sho Aoki"),
      voted_name: "Sho Aoki",
    },
    {
      voter_uuid: getUuidByName("Max Verstappen"),
      voter_name: "Max Verstappen",
      voted_uuid: getUuidByName("Akira menai"),
      voted_name: "Akira menai",
    },
  ],
};

export const getMockEventVoteInfo = (eventId: string): EventVoteInfo => {
  const event = mockSeasonEvents.find((e) => e.id === eventId);
  if (!event) {
    throw new Error(`Event ${eventId} not found in mock data`);
  }
  const entries = mockVoteStore[eventId] ?? [];
  return {
    event_group_id: event.event_group.id,
    event_name: event.title,
    event_ended_flg: event.event_ended_flg,
    datetime: event.datetime,
    participants: event.participants.map((name) => ({
      user_id: getUuidByName(name),
      dartslive_card_name: name,
    })),
    vote_statuses: entries.map((e) => ({
      voter: { user_id: e.voter_uuid, dartslive_card_name: e.voter_name },
      voted: { user_id: e.voted_uuid, dartslive_card_name: e.voted_name },
    })),
  };
};

export const addMockVote = (eventId: string, voted_user_id: string): void => {
  if (!mockVoteStore[eventId]) {
    mockVoteStore[eventId] = [];
  }
  const voterName = loginMemberProfile.dartslive_card_name;
  const voterUuid = getUuidByName(voterName);
  // 既存の自分の投票を削除してから追加（上書き）
  mockVoteStore[eventId] = mockVoteStore[eventId].filter(
    (e) => e.voter_uuid !== voterUuid,
  );
  mockVoteStore[eventId].push({
    voter_uuid: voterUuid,
    voter_name: voterName,
    voted_uuid: voted_user_id,
    voted_name: getNameByUuid(voted_user_id),
  });
};

export const getMockVoteHistories = (): VoteHistory[] => {
  const voterName = loginMemberProfile.dartslive_card_name;
  const histories: VoteHistory[] = [];
  for (const event of mockSeasonEvents) {
    if (!event.event_ended_flg) continue;
    const entries = mockVoteStore[event.id] ?? [];
    const myEntry = entries.find((e) => e.voter_name === voterName);
    if (!myEntry) continue;
    const winner = event.results.find((r) => r.singlesRank === 1);
    histories.push({
      event_id: event.id,
      event_name: event.title,
      voter_dartslive_card_name: voterName,
      voted_dartslive_card_name: myEntry.voted_name,
      winner_dartslive_card_name: winner?.name ?? null,
      is_correct: !!winner && winner.name === myEntry.voted_name,
    });
  }
  return histories.reverse();
};


